import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { addPublicURL } from '../../Functions/AddPublicURL';
import { formatEntityId } from '../../Functions/FormatEntityId';
import { getHotelFeatures } from '../../Functions/HotelFeatures';
import { setHotelFeatureConnection } from '../../redux/reducers/controllersReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';

import styles from './hotelFeaturesDetail.module.scss';
interface HotelFeaturesDetailProps {
    deviceId: number;
    onClose: () => void;
}

const HotelFeaturesDetail: React.FC<HotelFeaturesDetailProps> = ({ deviceId, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const devices = useAppSelector((state) => state.controllers.devices);
    const device = devices.find((item) => item.id === deviceId);
    if (!device) {
        return (
            <div className={styles.overlay} role="dialog" aria-labelledby="hotel-features-title">
                <div className={styles.content}>
                    <h2 id="hotel-features-title">{t('hotelFeaturesDetail.missingDevice')}</h2>
                    <Button size="sm" variant="fill" onClick={onClose}>{t('close')}</Button>
                </div>
            </div>
        );
    }

    const features = getHotelFeatures(device);
    const candidates = devices.filter((target) =>
        target.id !== device.id && (
            device.hotelFeatureDeviceIds?.includes(target.id)
            || target.hotelFeatureDeviceIds?.includes(device.id)
            || (target.tech === device.tech && getHotelFeatures(target).some((feature) => features.includes(feature)))
        )
    );

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="hotel-features-title" aria-describedby="hotel-features-description">
            <div className={styles.content}>
                <header className={styles.header}>
                    <h2 id="hotel-features-title">{t('hotelFeaturesDetail.title')}</h2>
                    <p id="hotel-features-description">{t('hotelFeaturesDetail.description')}</p>
                </header>
                <div className={styles.source}>
                    <img className={styles.preview} src={addPublicURL(`/assets/controllers/preview/${device.previewImage}-${device.color}.png`)} alt={`${device.name}${device.colorShortcut}`} />
                    <p>{device.name}{device.colorShortcut} | {formatEntityId('controller', device.id)}</p>
                </div>
                {candidates.length === 0 && <p>{t('hotelFeaturesDetail.empty')}</p>}
                <div className={styles.devices}>
                    {candidates.map((target) => {
                        const shared = getHotelFeatures(target).filter((feature) => features.includes(feature));
                        return (
                            <div key={target.id} className={styles.device}>
                                <img className={styles.preview} src={addPublicURL(`/assets/controllers/preview/${target.previewImage}-${target.color}.png`)} alt={`${target.name}${target.colorShortcut}`} />
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <Checkbox
                                            variant="outline"
                                            checked={Boolean(device.hotelFeatureDeviceIds?.includes(target.id) || target.hotelFeatureDeviceIds?.includes(device.id))}
                                            onChange={(connected) => {
                                                if (connected && (target.tech !== device.tech || shared.length === 0)) {
                                                    toast.error(t('hotelFeaturesDetail.incompatible'));
                                                    return;
                                                }
                                                dispatch(setHotelFeatureConnection({ sourceId: device.id, targetId: target.id, connected }));
                                            }}
                                        />
                                        <p>{`${target.name}${target.colorShortcut} | ${formatEntityId('controller', target.id)}`}</p>
                                    </div>
                                    <span>{shared.map((feature) => t(`hotelFeaturesDetail.features.${feature}`)).join(', ')}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.actions}>
                    <Button size="md" variant="fill" onClick={onClose}>{t('apply')}</Button>
                </div>
            </div>
        </div>
    );
};

export default HotelFeaturesDetail;
